// Tracking manifest client — consumes the per-team, entitlement-scoped
// `GET /game/session:{id}/tracking` manifest that replaced the retired
// single-file `/packets` endpoint. Kept RTK-free (plain fetch + broadcast base
// URL) like the sibling download helpers so every app can call it regardless
// of its server config. Types live here, not in `types.ts`, because they model
// a broadcast-API response this module owns end-to-end.
import {
  broadcastSessionUrl,
  buildFilename,
  navigateToSignedUrl
} from './downloads'

/** One downloadable file in a team's tracking set: a signed GCS URL + expiry. */
export interface TrackingArtifact {
  object: string
  link: string
  expiry: string
}

/**
 * A team's tracking delivery. All three artifacts are optional-and-nullable
 * regardless of what the generated OpenAPI schema says: the backend omits or
 * nulls them when a session has no high-rate tier, when tracking is withheld,
 * or transitionally before the producer's copy path is deployed. Never assume
 * any are present — guard with `!= null`.
 */
export interface TeamTrackingSet {
  teamId: string
  teamName?: string
  status: 'delivered' | 'withheld' | 'not_instrumented'
  metadata?: TrackingArtifact | null
  standardTracking?: TrackingArtifact | null
  highRateBall?: TrackingArtifact | null
}

export interface TrackingManifest {
  sessionId: string
  schemaVersion: string
  teams: TeamTrackingSet[]
}

/**
 * Discriminated result so callers render the three empty states without
 * re-reading `res.status`:
 * - `entitled`       — 200 with a manifest (its teams may still be non-delivered)
 * - `none-entitled`  — 403, the caller is entitled to no team on this session
 * - `never-exported` — 404, tracking was never exported for this session
 */
export type TrackingResult =
  | { kind: 'entitled'; manifest: TrackingManifest }
  | { kind: 'none-entitled' }
  | { kind: 'never-exported' }

export interface GetTrackingOptions {
  /** Narrows the manifest to a single team (`?team=<id>`). */
  team?: string
  /** Org scope forwarded as `?orgId=<orgId>`, same as the session/event endpoints. */
  orgId?: string
}

function trackingQuery(options?: GetTrackingOptions) {
  const query = Object.entries({ team: options?.team, orgId: options?.orgId })
    .filter((entry): entry is [string, string] => {
      const [, value] = entry
      return value != null && value !== ''
    })
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')

  return query ? `?${query}` : ''
}

/**
 * Fetches the tracking manifest for a session. Maps 403/404 to their own
 * result kinds so the panel's empty states don't re-read the status code.
 */
export async function getTracking(
  baseUrl: string,
  sessionId: string,
  options?: GetTrackingOptions
): Promise<TrackingResult> {
  const res = await fetch(
    broadcastSessionUrl(
      baseUrl,
      sessionId,
      `/tracking${trackingQuery(options)}`
    ),
    { credentials: 'include', headers: { Accept: 'application/json' } }
  )
  if (res.status === 403) return { kind: 'none-entitled' }
  if (res.status === 404) return { kind: 'never-exported' }
  if (!res.ok) {
    throw new Error(`Failed to fetch tracking manifest: ${res.statusText}`)
  }
  const manifest = (await res.json()) as TrackingManifest

  return { kind: 'entitled', manifest }
}

/** A team's present artifacts, in display order, skipping absent/null ones. */
export function presentArtifacts(team: TeamTrackingSet): TrackingArtifact[] {
  return [team.metadata, team.standardTracking, team.highRateBall].filter(
    (artifact): artifact is TrackingArtifact => artifact != null
  )
}

/**
 * Downloads tracking artifacts, one anchor per artifact. The panel downloads a
 * single file per user click (browsers reliably handle one cross-origin
 * download per gesture but silently drop most of a programmatic multi-file
 * burst — the durable multi-file fix is a single server-side zip; see the
 * tracking-download archive follow-up), so this is normally called with one
 * artifact. Pass `presentArtifacts(team)` for a whole-team download.
 */
export function downloadTrackingArtifacts(
  artifacts: TrackingArtifact[],
  sessionDate: string,
  teamName: string
): void {
  for (const artifact of artifacts) {
    navigateToSignedUrl(
      artifact.link,
      buildFilename(sessionDate, teamName, artifact.object)
    )
  }
}
